import React, { useState, useEffect } from "react";
import { PackageOpen, ArrowLeft, Filter, Search, Home } from "lucide-react";
import { motion } from "framer-motion";
import http from '../../Api/http';
import StatusBadge from "./StatusBadge";
import { useAuth } from '../AuthContext';
import { useTranslation } from "react-i18next";
import Shipment from "./Shipment";
import Util from "../../Util";
import { Navigate, useNavigate } from "react-router-dom";


const ManagerDashboard = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, loadingUser } = useAuth();
    const [shipments, setShipments] = useState([]);
    const [shipmentId, setShipmentId] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [openShipmentDialog, setOpenShipmentDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState(0);    
    const StatusList = Util.getShipmentStatusList(t);

    useEffect(() => {
        if (user?.ID) {
            loadShipments(0);
        }
    }, [user]);

    const loadShipments = async (statusId) => {
        try {
            setIsLoading(true);
            const obj = {
                StatusId: statusId || 0
            }
            const res = await http.getJsonAxios(http.actions.Shipments.GetOrdersForMonitor, obj, t);
            
            if (res.status === 200 && res.data.success) {
                setShipments(res.data.obj);
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };

    const filtered = shipments.filter((s) =>
        s.Id?.toString().includes(searchTerm) ||
        (s.SupplierName && s.SupplierName.includes(searchTerm))
    );

    if (loadingUser) return null;
    if (!user) return <Navigate to="/" />;

    return (
        <div className="space-y-6 max-w-6xl mx-auto pt-10 pb-20" dir={t('dir')}>
            <div className="container px-4 mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white items-center gap-2">
                        <button
                            onClick={() => navigate("/SpareParts")}
                            className="hover:text-primary transition"
                            title={t('myOrders')}
                        >
                            <Home className="w-9 h-9 text-blue-500 hover:text-blue-700" />
                        </button>
                        {t("monitorShipments")}
                    </h1>
                    <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        {t('monitorShipmentsSubTitle')}
                    </p>
                </motion.div></div>

            {/* Header + Search */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 bg-white dark:bg-gray-800 p-6 rounded-2xl border shadow-sm"
                dir={t('dir')}>
                <div className="flex w-full md:w-auto gap-3">
                    <div className="relative w-full md:w-64">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            placeholder={t('shipmentSearchHint')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pr-9 border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>

                    <button className="shrink-0">
                        <Filter className="w-4 h-4 text-gray-500" />
                    </button>
                </div>
                <div className="flex w-full md:w-auto gap-3">
                    <select value={statusFilter} onChange={e => {
                        setStatusFilter(e.target.value);
                        loadShipments(e.target.value);
                    }}
                        className="border px-2 rounded w-full border rounded focus:ring-2 focus:ring-blue-500 outline-none" dir={t('dir')}>
                        {StatusList.map(status => (
                            <option key={status.Id} value={status.Id}>
                                {status.Name}
                            </option>
                        ))}
                    </select>
                    <p className="text-gray-500 text-sm mt-1">
                    </p>
                </div>
            </div>

            {/* Loading */}
            {isLoading ? (
                <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                        <div
                            key={i}
                            className="h-20 w-full animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl"
                        />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-gray-500">
                    {t('noMatchedData')}
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <div className="w-full">
                            {/* Desktop Table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-sm text-right">
                                    <thead className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 uppercase border-b">
                                        <tr>
                                            <th className="px-6 py-4 font-semibold">{t('shipmentNo')}</th>
                                            <th className="px-6 py-4 font-semibold">{t('theSupplier')}</th>
                                            <th className="px-6 py-4 font-semibold">{t('status')}</th>
                                            <th className="px-6 py-4 font-semibold">{t('createDate')}</th>
                                            <th className="px-6 py-4 font-semibold">{t('sendType')}</th>
                                            <th className="px-6 py-4 text-left"></th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y">
                                        {filtered.map((shipment, i) => (
                                            <motion.tr
                                                key={shipment.Id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.03 }}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition group"
                                                onClick={() => {
                                                    setShipmentId(shipment.Id);
                                                    setOpenShipmentDialog(true);
                                                }}
                                            >
                                                <td className="px-6 py-4 font-mono font-bold">
                                                    #{shipment.Id}
                                                </td>

                                                <td className="px-6 py-4 font-medium">
                                                    {shipment.SupplierName}
                                                </td>

                                                <td className="px-6 py-4">
                                                    <StatusBadge status={shipment.StatusId} />
                                                </td>

                                                <td className="px-6 py-4 text-gray-500">
                                                    {Util.formatDate(shipment.InsDate, i18n.language)}
                                                </td>

                                                <td className="px-6 py-4">
                                                    {shipment.StatusId === 1 ? (
                                                        <span className="text-gray-400"></span>
                                                    ) : shipment.DeliveryType === "employee" ? (
                                                        <span className="text-blue-600 font-medium">
                                                            {t('employee')}: {shipment.EmployeeName}
                                                        </span>
                                                    ) : shipment.DeliveryType === "taxi_driver" ? (
                                                        <span className="text-amber-600 font-medium">
                                                            {t('driverName')}: {shipment.DriverName}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400"></span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 text-left">
                                                    <button
                                                        className="inline-flex items-center text-blue-600 font-medium hover:text-green-700 transition"
                                                    >
                                                        {t('details')}
                                                        <ArrowLeft className="w-4 h-4 ms-1 rtl:-scale-x-100 group-hover:-translate-x-1" />
                                                    </button>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="md:hidden space-y-4">
                                {filtered.map((shipment, i) => (
                                    <motion.div
                                        key={shipment.Id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.03 }}
                                        className="bg-white dark:bg-gray-800 rounded-2xl shadow p-4 space-y-3"
                                        onClick={() => {
                                            setShipmentId(shipment.Id);
                                            setOpenShipmentDialog(true);
                                        }}
                                    >
                                        <div className="flex justify-between">
                                            <span className="font-bold">#{shipment.Id}</span>
                                            <StatusBadge status={shipment.StatusId} />
                                        </div>

                                        <div>
                                            <span className="text-gray-400 text-xs">{t('theSupplier')}</span>
                                            <div className="font-medium">
                                                {shipment.SupplierName}
                                            </div>
                                        </div>

                                        <div>
                                            <span className="text-gray-400 text-xs">{t('createDate')}</span>
                                            <div>
                                                {Util.formatDate(shipment.InsDate, i18n.language)}
                                            </div>
                                        </div>

                                        <div>
                                            <span className="text-gray-400 text-xs">{t('sendType')}</span>
                                            <div>
                                                {shipment.StatusId === 1 ? (
                                                    <span className="text-gray-400"></span>
                                                ) : shipment.DeliveryType === "employee" ? (
                                                    <span className="text-blue-600 font-medium">
                                                        {t('employee')}: {shipment.EmployeeName}
                                                    </span>
                                                ) : shipment.DeliveryType === "taxi_driver" ? (
                                                    <span className="text-amber-600 font-medium">
                                                        {t('driverName')}: {shipment.DriverName}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400"></span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="pt-2 border-t">
                                            <button className="flex items-center text-blue-600 font-medium">
                                                {t('details')}
                                                <ArrowLeft className="w-4 h-4 ms-1 rtl:-scale-x-100" />
                                            </button>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Shipment Dialog */}
            {
                openShipmentDialog &&
                <Shipment
                    open={openShipmentDialog}
                    onClose={() => {
                        setOpenShipmentDialog(false);
                        loadShipments(statusFilter);
                    }}
                    shipmentId={shipmentId}  // 0 تعني شحنة جديدة
                />
            }
        </div>
    );
}

export default ManagerDashboard;